import { useEffect, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { getCurrentCompanyAusIqSummaryAsync } from "features/company/companySlice";
import { Box, Flex, Grid, Heading, Link, Spinner, Text } from "theme-ui";
import { numberWithCommas } from "utils/helpers";

const mapStateToProps = (state) => ({
    company: state.companies?.currentCompany,
    companies: state.companies,
});

const Summary = ({ company, companies }) => {
    const dispatch = useDispatch();
    const prevAsxRef = useRef();

    const { summary, summaryStatus } =
        companies?.currentCompanyExtras?.ausIq ?? {};

    useEffect(() => {
        const asx = company?.asx;
        if (!asx) return;

        const asxChanged = prevAsxRef.current !== asx;

        if (summaryStatus === "idle" || asxChanged) {
            dispatch(getCurrentCompanyAusIqSummaryAsync(asx));
        }

        prevAsxRef.current = asx;
    }, [company?.asx, dispatch, summaryStatus]);

    if (summaryStatus === "loading") {
        return (
            <Flex
                sx={{
                    flexDirection: "column",
                }}
            >
                <Spinner variant="styles.spinner" mx={"auto"} mt={4} />
            </Flex>
        );
    }

    return (
        <Grid gap={4} columns={[1, null, 3]}>
            {summary ? (
                <>
                    {/* // Ticker */}
                    <Box>
                        <Text
                            as="p"
                            variant="h4"
                            sx={{ textTransform: "capitalize" }}
                        >
                            Ticker
                        </Text>
                        <Heading as="h2" variant="h3" sx={{ mt: 2 }}>
                            {summary.ticker || "-"}
                        </Heading>
                    </Box>

                    {/* Company Name */}
                    <Box>
                        <Text
                            as="p"
                            variant="h4"
                            sx={{ textTransform: "capitalize" }}
                        >
                            Company Name
                        </Text>
                        <Heading as="h2" variant="h3" sx={{ mt: 2 }}>
                            {summary.company_name || "-"}
                        </Heading>
                    </Box>

                    {/* Industry */}
                    <Box>
                        <Text
                            as="p"
                            variant="h4"
                            sx={{ textTransform: "capitalize" }}
                        >
                            Industry
                        </Text>
                        <Heading as="h2" variant="h3" sx={{ mt: 2 }}>
                            {summary.industry || "-"}
                        </Heading>
                    </Box>

                    {/* Market Cap */}
                    <Box>
                        <Text
                            as="p"
                            variant="h4"
                            sx={{ textTransform: "capitalize" }}
                        >
                            Market Cap
                        </Text>
                        <Heading as="h2" variant="h3" sx={{ mt: 2 }}>
                            {summary.market_cap
                                ? `$${numberWithCommas(summary.market_cap)}M`
                                : "-"}
                        </Heading>
                    </Box>

                    {/* Share Price */}
                    <Box>
                        <Text
                            as="p"
                            variant="h4"
                            sx={{ textTransform: "capitalize" }}
                        >
                            Share Price (Prev. Close)
                        </Text>
                        <Heading as="h2" variant="h3" sx={{ mt: 2 }}>
                            {company?.share_price
                                ? `$${numberWithCommas(company?.share_price)}`
                                : "-"}
                        </Heading>
                    </Box>

                    {/* Shares Issued */}
                    <Box>
                        <Text
                            as="p"
                            variant="h4"
                            sx={{ textTransform: "capitalize" }}
                        >
                            Shares Issued
                        </Text>
                        <Heading as="h2" variant="h3" sx={{ mt: 2 }}>
                            {summary.shares_issued_mln
                                ? numberWithCommas(summary.shares_issued_mln) +
                                  "M"
                                : "-"}
                        </Heading>
                    </Box>

                    {/* 52W Low/High */}
                    <Box>
                        <Text
                            as="p"
                            variant="h4"
                            sx={{ textTransform: "capitalize" }}
                        >
                            52W Low / High
                        </Text>
                        <Heading as="h2" variant="h3" sx={{ mt: 2 }}>
                            {summary.price_52W_High && summary.price_52W_Low
                                ? `$${numberWithCommas(
                                      summary.price_52W_Low
                                  )} / $${numberWithCommas(
                                      summary.price_52W_High
                                  )}`
                                : "-"}
                        </Heading>
                    </Box>

                    {/* Website */}
                    <Box>
                        <Text
                            as="p"
                            variant="h4"
                            sx={{ textTransform: "capitalize" }}
                        >
                            Website
                        </Text>
                        <Heading as="h2" variant="h3" sx={{ mt: 2 }}>
                            {summary.web_url ? (
                                <Link
                                    href={summary.web_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ fontWeight: "normal", fontSize: [3, 4] }}
                                >
                                    {summary.web_url}
                                </Link>
                            ) : (
                                "-"
                            )}
                        </Heading>
                    </Box>
                </>
            ) : (
                <Text as="p" variant="h6">
                    No summary data available.
                </Text>
            )}
        </Grid>
    );
};

export default connect(mapStateToProps)(Summary);
